import React, { useEffect, useState, useRef } from 'react';

/**
 * CalendarSlotTooltip - Tooltip for hovering over calendar slots
 * Smart positioning: appears above slot if there's room, below otherwise
 */
const CalendarSlotTooltip = ({
  hoveredSlot,
  tooltipPosition,
  services,
  servers,
  isAdmin,
  calendar,
  user
}) => {
  const tooltipRef = useRef(null);
  const [adjustedPosition, setAdjustedPosition] = useState({ x: 0, y: 0, showAbove: true });

  useEffect(() => {
    if (!tooltipPosition || !tooltipRef.current) return;

    const tooltipHeight = tooltipRef.current.offsetHeight;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const tooltipWidth = tooltipRef.current.offsetWidth;
    
    // Calculate if tooltip would go off the top of the viewport
    const spaceAbove = tooltipPosition.y;
    const spaceBelow = viewportHeight - tooltipPosition.y - 80; // 80px approx slot height
    
    // Determine if we should show above or below
    const showAbove = spaceAbove > tooltipHeight + 10;
    
    // Calculate X position (keep tooltip within viewport)
    let xPos = tooltipPosition.x;
    if (xPos + tooltipWidth > viewportWidth - 20) {
      xPos = viewportWidth - tooltipWidth - 20;
    }
    if (xPos < 10) {
      xPos = 10;
    }
    
    // Calculate Y position
    let yPos;
    if (showAbove) {
      yPos = tooltipPosition.y - 10; // 10px gap above the slot
    } else {
      yPos = tooltipPosition.y + 90; // Below the slot (approx 80px slot height + 10px gap)
    }
    
    setAdjustedPosition({ x: xPos, y: yPos, showAbove });
  }, [tooltipPosition]);

  if (!hoveredSlot || !tooltipPosition) return null;

  return (
    <div
      ref={tooltipRef}
      className="fixed bg-white shadow-xl rounded-md border border-gray-300 p-3 z-[100] min-w-[200px] max-w-[300px]"
      style={{
        left: `${adjustedPosition.x}px`,
        top: `${adjustedPosition.y}px`,
        transform: adjustedPosition.showAbove ? 'translateY(-100%)' : 'translateY(0)'
      }}
    >
      {tooltipPosition.appointments && tooltipPosition.appointments.length > 0 ? (
        // Tooltip for booked slots - show appointment details
        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-700 mb-2">
            Booking Details:
          </div>
          {(() => {
            // Group appointments by service_id + start_time for multi-customer display
            const groups = {};
            tooltipPosition.appointments.forEach(apt => {
              const key = `${apt.service_id}-${apt.start_time}`;
              if (!groups[key]) {
                groups[key] = {
                  service_id: apt.service_id,
                  service_name: apt.service_name,
                  start_time: apt.start_time,
                  notes: apt.notes,
                  customers: []
                };
              }
              groups[key].customers.push({
                id: apt.customer_id,
                name: apt.customer_name
              });
            });
            
            return Object.values(groups).map((group, idx) => {
              const service = services.find(s => s.id === group.service_id);
              const maxCustomers = service?.customers_allowed || 1;
              const currentCustomers = group.customers.length;
              const isMissingParticipants = maxCustomers > 1 && currentCustomers < maxCustomers;
              
              return (
                <div key={idx} className="border-t pt-2 first:border-t-0 first:pt-0">
                  <div className="flex items-center text-xs mb-1">
                    <div
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: service?.color_code || '#3B82F6' }}
                    ></div>
                    <span className="font-medium text-gray-800">{group.service_name}</span>
                  </div>
                  {isAdmin && (
                    <>
                      <div className="text-xs text-gray-600 ml-5">
                        {maxCustomers > 1 ? (
                          <>
                            <span className="font-medium">Customers ({currentCustomers}/{maxCustomers}):</span>
                            <ul className="mt-1 ml-2">
                              {group.customers.map((cust, custIdx) => (
                                <li key={custIdx}>• {cust.name}</li>
                              ))}
                            </ul>
                          </>
                        ) : (
                          <>Customer: {group.customers[0]?.name}</>
                        )}
                      </div>
                      {/* Missing participant warning */}
                      {isMissingParticipants && (
                        <div className="text-xs text-orange-600 ml-5 mt-1 flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Missing {maxCustomers - currentCustomers} participant(s)
                        </div>
                      )}
                      {group.notes && (
                        <div className="text-xs text-gray-600 ml-5 mt-1">
                          <span className="font-medium">Note:</span> {group.notes}
                        </div>
                      )}
                    </>
                  )}
                  {!isAdmin && (
                    <div className="text-xs text-gray-500 ml-5 italic">
                      {group.customers.some(c => c.id === user?.id) 
                        ? 'Your booking' 
                        : 'Booked by another customer'}
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </div>
      ) : (
        // Tooltip for available slots or On Hold slots
        <div className="space-y-3">
          {/* On Hold Badge */}
          {tooltipPosition.isOnHold && (
            <div className="flex items-center text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-1 rounded">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              On Hold (Hidden from Customers)
            </div>
          )}
          
          {/* Available Services */}
          <div>
            <div className="text-xs font-semibold text-gray-700 mb-2">
              {tooltipPosition.isOnHold ? 'Services (Admin Only):' : 'Available Services:'}
            </div>
            {services.length > 0 ? (
              <div className="space-y-1">
                {services.map((service) => (
                  <div key={service.id} className="flex items-center text-xs">
                    <div
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: service.color_code }}
                    ></div>
                    <span className="text-gray-800">{service.service_name}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-gray-500 italic">NONE</div>
            )}
          </div>
          
          {/* Available Servers (Admin only, Multiple Servers mode) */}
          {isAdmin && calendar?.multiple_servers && tooltipPosition.availableServerIds && tooltipPosition.availableServerIds.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-700 mb-2">Available Servers:</div>
              <div className="space-y-1">
                {tooltipPosition.availableServerIds.map((serverId) => {
                  const server = servers.find(s => s.id === serverId);
                  return server ? (
                    <div key={serverId} className="flex items-center text-xs">
                      <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                      <span className="text-gray-800">{server.server_name}</span>
                    </div>
                  ) : null;
                })}
              </div>
            </div>
          )}
          
          {/* No Servers Available Message */}
          {isAdmin && calendar?.multiple_servers && tooltipPosition.availableServerIds && tooltipPosition.availableServerIds.length === 0 && (
            <div>
              <div className="text-xs font-semibold text-orange-600 mb-1">⚠️ No Servers Available</div>
              <div className="text-xs text-gray-600">All servers are busy or none are working at this time.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CalendarSlotTooltip;
